import { isSupportedItemType } from "./../../../utils/item-type-checker";
import * as A from "~/parse/sfml-ast";
import * as S from "~/styles/sfml.css";
import * as C from "~/constants";
import * as E from "~/renderer/web-components/element";
import * as F from "./fonts";

import { ViewportObserver } from "~/renderer/web-components/viewport";
import { FlyerImage, FlyerImageAnchor, LinkProvider } from "~/renderer/web-components/image";
import { ItemAtom } from "~/renderer/web-components/item-atom";
import { WayfinderControl } from "~/renderer/web-components/wayfinder";

const WAYFINDER_ANCHOR_BUMP = 0.001;

export class Storefront extends E.SFMLElement {
  private titleText: string | null = null;
  private subtitleText: string | null = null;
  private annotationRegistry = new Map<string, string>();

  private evEventTimeout: number | null = null;
  private viewEventTimeout: number | null = null;
  private opened: boolean = false;
  private engaged: boolean = false;
  private tracking: boolean = false;

  private _wayfinder: WayfinderControl | null = null;

  private lastScrollY = 0;
  // TODO: this variable is not being used anywhere??? should be removed?
  private scrollUpdatePending = false;

  private get wayfinder() {
    if (this._wayfinder) return this._wayfinder;

    const wayfinder = this.querySelector("[sfml-wayfinder]") as any;

    if (wayfinder) this._wayfinder = wayfinder as WayfinderControl;

    return this._wayfinder;
  }

  constructor() {
    super();
  }

  static observedAttributes = ["title", "subtitle"];

  public get subtitle() {
    return this.subtitleText;
  }

  connectedCallback() {
    this.addEventListener(C.ITEM_CLICK_EVENT, this.onItemClickHandler as EventListener);
    this.addEventListener(C.ANCHOR_REQUEST_EVENT, this.onAnchorRequest as EventListener);
    this.addEventListener(C.WAYFINDER_SCROLL_EVENT, this.onWayfinderScroll as EventListener);
    ViewportObserver.shared.listenScroll(this.onWindowScroll);
    E.AspectResizer.shared.listen(this.resizeStorefrontHandler);
  }

  disconnectedCallback() {
    this.removeEventListener(C.ITEM_CLICK_EVENT, this.onItemClickHandler as EventListener);
    this.removeEventListener(C.WAYFINDER_SCROLL_EVENT, this.onWayfinderScroll as EventListener);
    this.removeEventListener(C.ANCHOR_REQUEST_EVENT, this.onAnchorRequest as EventListener);
    ViewportObserver.shared.unlistenScroll(this.onWindowScroll);
    E.AspectResizer.shared.unlisten(this.resizeStorefrontHandler);
    this.stopTracking();
  }

  attributeChangedCallback(attr: string, oldValue: string | null, newValue: string | null) {
    switch (attr) {
      case "title": {
        this.titleText = newValue;
        break;
      }
      case "subtitle": {
        this.subtitleText = newValue;
        break;
      }
    }
  }

  public setAssetUrl(url: string) {
    FlyerImage.setAssetUrl(url);
  }

  public setLinkProvider(linkProvider: LinkProvider) {
    FlyerImage.setLinkProvider(linkProvider);
  }

  private onItemClickHandler = (e: CustomEvent) => {
    const itemType = e.detail.itemType;
    if (!itemType || !isSupportedItemType(itemType)) return;

    const itemId = e.detail.itemId;
    if (!itemId) return;

    let isTargetAnchorTap = false;

    const targetAnchorId = e.detail.targetAnchorId;
    if (targetAnchorId) {
      const event = new CustomEvent(C.ANCHOR_REQUEST_EVENT, {
        bubbles: true,
        detail: { anchorId: `sfml_anchor_${targetAnchorId}` },
      });
      this.dispatchEvent(event);

      isTargetAnchorTap = true;
    }

    if (this.tracking) this.sendEngagementIfUnsent();
    const payload = e.detail.payload;

    const event = new CustomEvent(C.STOREFRONT_TAP_EVENT, {
      bubbles: true,
      detail: { itemType, itemId, payload, isTargetAnchorTap },
    });
    this.dispatchEvent(event);
  };

  private resizeStorefrontHandler = (): void => {
    if (!this.wayfinder) return;

    // UGLY - This value is sometimes wrong when resizing to the initial
    // size after a reload - the scroll positon stored inside the viewport
    // observer is wrong - and there is no event to update this value so
    // we force it to refresh here.
    ViewportObserver.shared.refresh();
    this.lastScrollY = ViewportObserver.shared.scrollY;

    const anchorPositions: { anchorId: string; percent: number }[] = [];
    const anchors = this.querySelectorAll(`a[name]`);
    for (let i = 0, n = anchors.length; i < n; ++i) {
      const anchor = anchors[i];

      const anchorId = anchor.getAttribute("name");
      const top = this.lastScrollY - this.offsetTop;
      const crect = ViewportObserver.shared.getBoundingClientRect(anchor);
      const relativeAnchorTop = crect.top;
      const percent = (top + relativeAnchorTop) / this.offsetHeight - WAYFINDER_ANCHOR_BUMP;

      if (anchorId !== null) {
        anchorPositions.push({ anchorId, percent });
      }
    }

    this.wayfinder.setAnchorPositions(anchorPositions);
  };

  private stopTracking() {
    if (!this.tracking) return;
    this.opened = false;
    this.engaged = false;

    if (this.evEventTimeout) clearTimeout(this.evEventTimeout);

    if (this.viewEventTimeout) clearTimeout(this.viewEventTimeout);

    this.tracking = false;
  }

  private sendOpenIfUnsent() {
    if (this.opened) return;

    if (!this.tracking) return;

    const event = new CustomEvent(C.STOREFRONT_OPEN_EVENT, { bubbles: true });
    this.dispatchEvent(event);

    this.opened = true;
  }

  private sendEngagementIfUnsent = (): void => {
    if (this.evEventTimeout) clearTimeout(this.evEventTimeout);

    if (this.engaged) return;

    if (!this.tracking) return;

    const event = new CustomEvent(C.STOREFRONT_EV_EVENT, {
      bubbles: true,
    });
    this.dispatchEvent(event);

    this.engaged = true;

    this.evEventTimeout = null;
  };

  private lastUserZoom: number | null = null;

  private cooldownScrollEvent = (): void => {
    if (this.tracking) {
      this.sendEngagementIfUnsent();
      this.sendViewEvent();
    }

    const userZoom = ViewportObserver.shared.viewWidth;
    if (userZoom !== this.lastUserZoom) {
      this.invalidateFlyerImages();
      this.lastUserZoom = userZoom;
    }
  };

  private invalidateFlyerImages(): void {
    const children = this.querySelectorAll(`sfml-flyer-image`);
    for (let i = 0, n = children.length; i < n; ++i) {
      const flyerImage = children[i];

      if (!(flyerImage instanceof FlyerImage) || !flyerImage.visible) continue;

      flyerImage.handleResize();
    }
  }

  private sendViewEvent = (): void => {
    if (!this.tracking) return;

    const children = this.querySelectorAll(`[impressionable]`);
    let visibleItems: A.ItemIdentifier[] = [];

    const b = {
      left: 0,
      top: 0,
      width: ViewportObserver.shared.viewWidth,
      height: ViewportObserver.shared.viewHeight,
    };

    for (let i = 0, n = children.length; i < n; ++i) {
      const impressionable = children[i];
      if (!(impressionable instanceof FlyerImage) && !(impressionable instanceof ItemAtom)) continue;

      if (impressionable instanceof FlyerImage && !impressionable.visible) continue;

      const clientRects = impressionable.clientRects();

      for (let { identifier, rect: a } of clientRects) {
        const x0 = Math.max(a.left, b.left);
        const x1 = Math.min(a.left + a.width, b.left + b.width);

        if (x0 <= x1) {
          const y0 = Math.max(a.top, b.top);
          const y1 = Math.min(a.top + a.height, b.top + b.height);

          if (y0 <= y1) {
            const intWidth = x1 - x0;
            const intHeight = y1 - y0;

            const origArea = a.width * a.height * C.ANALYTICS_ITEM_COVERAGE;
            const viewArea = b.width * b.height * C.ANALYTICS_VIEWPORT_COVERAGE;
            const intArea = intWidth * intHeight;

            if (intArea > origArea || intArea > viewArea) visibleItems.push(identifier);
          }
        }
      }
    }

    const thisRect = ViewportObserver.shared.getBoundingClientRect(this);
    const viewportBottomOffset = Math.min(-thisRect.top + b.height, this.offsetHeight);

    if (viewportBottomOffset > 0) {
      const event = new CustomEvent(C.STOREFRONT_SCROLL_EVENT, {
        bubbles: true,
        detail: { storefrontHeight: this.offsetHeight, viewportBottomOffset },
      });
      this.dispatchEvent(event);
    }

    const event = new CustomEvent(C.STOREFRONT_VIEW_EVENT, {
      bubbles: true,
      detail: { visibleItems },
    });
    this.dispatchEvent(event);

    this.viewEventTimeout = null;
  };

  private onWindowScroll = (scrollY: number, scrollX: number) => {
    this.lastScrollY = scrollY;

    if (this.viewEventTimeout) clearTimeout(this.viewEventTimeout);

    if (this.tracking) this.viewEventTimeout = window.setTimeout(this.cooldownScrollEvent, C.ANALYTICS_VIEW_TIMER);

    if (!this.wayfinder) return;

    const top = (this.lastScrollY - this.offsetTop) / this.offsetHeight;
    const clampedTopPercentage = Math.min(Math.max(top, 0), 100);

    this.wayfinder.setScrollPosition(clampedTopPercentage);
  };

  private onWayfinderScroll = (e: CustomEvent): void => {
    const { percent, done } = e.detail;

    if (percent === null) return;

    const top = this.offsetTop + percent * this.offsetHeight;
    if (done) {
      ViewportObserver.shared.scrollTo(this, 0, top);
    }
  };

  private onAnchorRequest = (e: CustomEvent): void => {
    const anchorId = e.detail.anchorId;

    if (!anchorId) return;

    this.scrollToAnchorId(anchorId);
  };

  public registerAnnotationType(type: string, imageUrl: string) {
    this.annotationRegistry.set(type, imageUrl);
  }

  private getAnnottableElements(itemType: string): Map<string, E.SFMLAnnotatable[]> {
    const annotatableElements = new Map<string, E.SFMLAnnotatable[]>();

    const children = this.querySelectorAll(`[annotatable][item-type=${itemType}][source-id]`);
    for (let i = 0; i < children.length; i++) {
      const child = children[i];

      if (!(child instanceof E.SFMLElement)) continue;

      const itemId = child.getAttribute("source-id");
      if (itemId && E.isSFMLAnnotatable(child)) {
        const elements = annotatableElements.get(itemId) || [];
        elements.push(child);
        annotatableElements.set(itemId, elements);
      }
    }

    return annotatableElements;
  }

  public startTracking() {
    if (this.tracking) return;
    this.tracking = true;
    this.sendOpenIfUnsent();

    this.evEventTimeout = window.setTimeout(this.sendEngagementIfUnsent, C.ANALYTICS_EV_TIMER);
    this.viewEventTimeout = window.setTimeout(this.sendViewEvent, 500);
  }
  public addAnnotations(type: string, itemType: string, itemIds: string[]) {
    const annotationImageUrl = this.annotationRegistry.get(type);

    if (!annotationImageUrl) return;

    const annotatableElements = this.getAnnottableElements(itemType);
    for (let itemId of itemIds) {
      const elements = annotatableElements.get(itemId);

      if (!elements) continue;

      for (let element of elements) {
        if (element && !element.isAnnotated()) element.annotate(annotationImageUrl);
      }
    }
  }

  public removeAnnotations(type: string, itemType: string, itemIds: string[]) {
    const annotationImageUrl = this.annotationRegistry.get(type);

    if (!annotationImageUrl) return;

    const annotatableElements = this.getAnnottableElements(itemType);
    for (let itemId of itemIds) {
      const elements = annotatableElements.get(itemId);

      if (!elements) continue;

      for (let element of elements) {
        if (element && element.isAnnotated()) element.clearAnnotation();
      }
    }
  }

  public scrollToFlyerItemId(itemId: string): boolean {
    const elements = this.querySelectorAll(`[source-id="${itemId}"]`);
    const element = elements[0];

    if (!element || !(element instanceof HTMLElement)) return false;

    if (element instanceof FlyerImageAnchor && element.anchor) ViewportObserver.shared.scrollIntoView(element.anchor);
    else if (element instanceof FlyerImageAnchor && element.parentElement)
      ViewportObserver.shared.scrollIntoView(element.parentElement);
    else ViewportObserver.shared.scrollIntoView(element);

    return true;
  }

  public getWayfinderCategoryAnchorId(wayfinderCategoryName: string): string | null {
    const elements = this.querySelectorAll(`sfml-wayfinder-category .${S.label}`);

    for (let i = 0, n = elements.length; i < n; ++i) {
      const element = elements[i];

      if (element.textContent === wayfinderCategoryName) {
        const parent = element.parentNode;

        if (!parent || !(parent instanceof HTMLElement)) continue;

        return parent.getAttribute("anchor-id");
      }
    }

    return null;
  }

  public scrollToAnchorId(anchorId: string): boolean {
    const elements = this.querySelectorAll(`a[name=${anchorId}]`);
    const element = elements[0];

    if (!element || !(element instanceof HTMLElement)) return false;

    const parentElement = element.parentElement;

    if (!parentElement) return false;

    ViewportObserver.shared.scrollIntoView(parentElement);
    return true;
  }
}
customElements.define("sfml-storefront", Storefront);

export type SourceAction = "clippings-click" | null;

// TODO: should we remove this interface???
export interface StorefrontDelegate {
  viewDidLoad: () => void;
  didEngagement: () => void;
  didScrollEnd: (itemsInView: A.ItemIdentifier[]) => void;
  didTap: (itemType: A.ItemType, itemId: string) => void;
}



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/renderer/web-components/storefront/index.ts